import { HTMLMotionProps, motion, useAnimation } from 'framer-motion'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { usePrevious } from '../Utils/UsePrevious'

export interface MainProps extends HTMLMotionProps<"div"> { }

/**
 * The Main component acts as a container for the primary content of the application when the user is logged in.
 * It excludes the sidebar and wraps all other content, ensuring a consistent and responsive layout structure.
 *
 * Key features of this component include:
 * - Default padding and margins for proper spacing.
 * - A maximum width constraint to maintain a clean and centered layout.
 * - Smooth animations triggered during page transitions.
 * - Automatic scroll to top when navigating between routes.
 *
 * Furthermore, it serves as the primary scrolling area, ensuring the scrollbar is correctly positioned by browsers
 * and enabling sticky elements within the main content to function as intended.
 */

const Main = (props: MainProps) => {
    const controls = useAnimation()
    const location = useLocation()
    const prevLocation = usePrevious(location)
    const mainRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (prevLocation?.pathname !== location.pathname) {
            if (mainRef.current) {
                mainRef.current.scrollTop = 0
            }

            controls.start({
                scale: [1.03, 1],
                opacity: [0, 0, 1]
            })
        }
    }, [location, prevLocation, controls])

    return (
        <div
            id='node_MAIN'
            ref={mainRef}
            className='
                flex flex-col flex-grow items-center
                relative
                h-[100dvh] overflow-y-auto overflow-x-hidden
        '>
            <motion.div
                {...props}
                initial={{ scale: 1.03, opacity: 0 }}
                animate={controls}
                transition={{ duration: 0.4 }}
                className="
                    mt-20 lg:mt-10
                    px-5 lg:px-10
                    w-full
                "/>
        </div>
    )
}
export default Main
